package com.eladfinish.bible.contest.screens.openQuestions.model

import com.eladfinish.bible.contest.appcontent.withGodNameHyphenated
import com.eladfinish.bible.contest.network.spreadsheet.GoogleSheetRowHeadersAndValues
import com.eladfinish.bible.contest.network.spreadsheet.GoogleSheetRowHeadersAndValues.Companion.indexOfColumnContains
import com.eladfinish.bible.contest.network.spreadsheet.GoogleSheetRowHeadersAndValues.Companion.toOnlyValues
import com.eladfinish.bible.contest.screens.openQuestions.NO_ANSWER_YET
import com.eladfinish.bible.contest.screens.openQuestions.STOP_HERE_LABEL
import com.eladfinish.bible.contest.screens.openQuestions.TABLE_OF_CONTENT
import com.eladfinish.bible.contest.utils.addGershaim
import com.eladfinish.bible.contest.utils.toOtiot
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

// The open-questions spreadsheets: the topics sheet and each topic's questions sheet. Shared by the
// app (GetTopicsUseCase, QuestionItemJson) and the website generator.

const val JSON_TOPIC = "שאלה"
const val JSON_PERSONAL = "אישי"
const val JSON_INDEX = "תשובה"
const val JSON_ID = "ID"
const val JSON_ITEMS = "items"

@Serializable
data class TopicItemEntry(
    @SerialName(JSON_TOPIC) val topic: String,
    @SerialName(JSON_PERSONAL) val personal: String,
    @SerialName(JSON_INDEX) val index: String,
    @SerialName(JSON_ID) val id: String,
    @SerialName(JSON_ITEMS) val itemsData: String,
)

@Serializable
data class TopicItem(
    @SerialName("נושא") val topic: String,
    @SerialName("אישי") val isPersonal: Boolean,
    @SerialName("גיליון") val index: Int,
    @SerialName("ID") val id: String,
    @SerialName("items") val items: List<String>,
)

/** The topics sheet's rows; rows without a topic or a sheet index are skipped. */
fun List<GoogleSheetRowHeadersAndValues>.toTopicItemEntries(): List<TopicItemEntry> =
    toOnlyValues().map {
        TopicItemEntry(
            topic = it[indexOfColumnContains(JSON_TOPIC)],
            personal = it[indexOfColumnContains(JSON_PERSONAL)],
            index = it[indexOfColumnContains(JSON_INDEX)],
            id = it[indexOfColumnContains(JSON_ID)],
            itemsData = it[indexOfColumnContains(JSON_ITEMS)],
        )
    }.filter {
        it.topic.isNotEmpty() && it.index.isNotEmpty()
    }

private val topicJson = Json { ignoreUnknownKeys = true }

/** The topics, without the table-of-contents row; personal topics ("*") only if [includePersonal]. */
fun List<TopicItemEntry>.toTopicItems(includePersonal: Boolean): List<TopicItem> =
    (if (firstOrNull()?.topic == TABLE_OF_CONTENT) drop(1) else this)
        .map {
            TopicItem(
                topic = it.topic,
                isPersonal = it.personal == "*",
                index = it.index.toDouble().toInt(),
                id = it.id,
                items = it.itemsData.takeIf { data -> data.isNotEmpty() }
                    ?.let { data -> topicJson.decodeFromString<List<String>>(data) }.orEmpty(),
            )
        }
        .filter { includePersonal || !it.isPersonal }

/** One question row of a topic's sheet, with the sheet's conventions applied. */
data class OpenQuestionRow(
    val question: String,
    val answer: String,
    val book: String,
    val chapter: String,
    val verse: String,
    val author: String,
    val regex: String,
) {
    /** No written answer: it's whatever [regex] finds in the app's search. */
    val answerIsSearch: Boolean get() = regex.isNotEmpty() && answer.isEmpty()

    /** Whether the sheet has an actual answer: not blank, and not the [NO_ANSWER_YET] placeholder (often *bold*). */
    val hasAnswer: Boolean get() = answer.replace("*", "").trim().let { it.isNotEmpty() && it != NO_ANSWER_YET }

    val sourceLabel: String get() = openQuestionSourceLabel(book, chapter, verse)
}

/** A question row from its (column title, value) pairs. */
fun List<Pair<String, String>>.toOpenQuestionRow(): OpenQuestionRow {
    var question = ""
    var answer = ""
    var book = ""
    var chapter = ""
    var verse = ""
    var author = ""
    var regex = ""
    forEach { (column, value) ->
        when (column.trim()) {
            "שאלה" -> question = value.withGodNameHyphenated()
            "תשובה" -> answer = value
            "ספר" -> book = value
            "פרק" -> chapter = value
            "פסוק" -> verse = value.toIntOrNull()?.toOtiot() ?: value
            "מחבר" -> author = value
            "ביטוי-רגולרי" -> regex = value
        }
    }
    return OpenQuestionRow(question, answer, book, chapter, verse, author, resolveQuestionRegex(regex, question))
}

/** '"' in the regex column means the regex is the question itself. */
fun resolveQuestionRegex(regex: String, question: String): String = if (regex == "\"") question else regex

/** A sheet's questions end at a row marked [STOP_HERE_LABEL], if there is one. */
fun <T> List<T>.untilStopMark(question: (T) -> String?): List<T> {
    val stopIndex = indexOfFirst { question(it).orEmpty().contains(STOP_HERE_LABEL) }
    return if (stopIndex == -1) this else take(stopIndex)
}

/** "[ספר פרק, פסוק]" — where a question's answer is found; empty without a book. */
fun openQuestionSourceLabel(book: String, chapter: String, verse: String): String =
    if (book.isNotEmpty()) {
        buildString {
            append("[$book")
            if (chapter.isNotBlank()) {
                append(" ${chapter.addGershaim()}")
                if (verse.isNotBlank()) {
                    append(", ${verse.addGershaim()}")
                }
            }
            append("]")
        }
    } else {
        ""
    }
